import { useEffect, useRef, useState } from 'react';
import { isTouchDevice } from '@/lib/quality';

// Tiny glowing cloud particle that grows into a small cloud over interactive elements,
// and releases a puff on click. Disabled on touch devices.
export function CustomCursor() {
  const dot = useRef();
  const [enabled, setEnabled] = useState(false);
  const state = useRef({ x: -100, y: -100, tx: -100, ty: -100, mode: 'dot' });

  useEffect(() => {
    if (isTouchDevice()) return;
    setEnabled(true);
    document.body.classList.add('custom-cursor');
    const s = state.current;
    const onMove = (e) => {
      s.tx = e.clientX; s.ty = e.clientY;
      const el = e.target.closest?.('a, button, input, textarea, select, [role="button"], [data-cursor]');
      s.mode = el ? (el.dataset.cursor === 'cloud' || el.tagName === 'BUTTON' || el.tagName === 'A' ? 'cloud' : 'ring') : 'dot';
    };
    const onClick = (e) => {
      for (let i = 0; i < 7; i++) {
        const p = document.createElement('span');
        const a = (i / 7) * Math.PI * 2, r = 18 + Math.random() * 22;
        p.style.cssText = `position:fixed;left:${e.clientX}px;top:${e.clientY}px;width:10px;height:10px;border-radius:999px;pointer-events:none;z-index:9999;background:radial-gradient(circle,rgba(255,255,255,.95),rgba(255,255,255,0));transform:translate(-50%,-50%);transition:transform .55s cubic-bezier(.2,.8,.2,1),opacity .55s ease;`;
        document.body.appendChild(p);
        requestAnimationFrame(() => { p.style.transform = `translate(calc(-50% + ${Math.cos(a) * r}px), calc(-50% + ${Math.sin(a) * r - 10}px)) scale(2.4)`; p.style.opacity = '0'; });
        setTimeout(() => p.remove(), 600);
      }
    };
    let raf;
    const loop = () => {
      s.x += (s.tx - s.x) * 0.22; s.y += (s.ty - s.y) * 0.22;
      const size = s.mode === 'cloud' ? 46 : s.mode === 'ring' ? 26 : 12;
      if (dot.current) {
        dot.current.style.transform = `translate(${s.x - size / 2}px, ${s.y - size / 2}px)`;
        dot.current.style.width = dot.current.style.height = `${size}px`;
        dot.current.style.opacity = s.mode === 'cloud' ? '0.9' : '1';
        dot.current.style.filter = s.mode === 'cloud' ? 'blur(4px)' : 'blur(0.5px)';
      }
      raf = requestAnimationFrame(loop);
    };
    loop();
    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('click', onClick);
    return () => { cancelAnimationFrame(raf); window.removeEventListener('pointermove', onMove); window.removeEventListener('click', onClick); document.body.classList.remove('custom-cursor'); };
  }, []);

  if (!enabled) return null;
  return <div ref={dot} className="fixed left-0 top-0 z-[9998] rounded-full pointer-events-none" style={{ width: 12, height: 12, background: 'radial-gradient(circle, rgba(255,255,255,1) 0%, rgba(255,255,255,0.55) 45%, rgba(255,255,255,0) 100%)', boxShadow: '0 0 18px 4px rgba(255,255,255,0.45), 0 0 34px 8px rgba(160,200,255,0.25)', transition: 'width .25s ease, height .25s ease, opacity .25s ease, filter .25s ease' }} data-testid="custom-cursor" />;
}
